import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import StatsCard from '@/components/Dashboard/StatsCard';
import { dashboardAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  CurrencyInr, 
  Receipt, 
  TrendUp, 
  Bank,
  CalendarBlank,
  DownloadSimple,
  ShoppingCart,
  CreditCard,
} from '@phosphor-icons/react';

export default function DailyTrackerPage() {
  const [loading, setLoading] = useState(true);
  const fmtLocal = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const today = fmtLocal(new Date());
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [dailySummary, setDailySummary] = useState(null);
  const [clinicBreakdown, setClinicBreakdown] = useState([]);
  const [sourceBreakdown, setSourceBreakdown] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardAPI.getDailySummary(null, null, startDate, endDate);
      setDailySummary(response.data);
      setClinicBreakdown(response.data.clinic_breakdown || []);
      setSourceBreakdown(response.data.source_breakdown || []);
    } catch (error) {
      console.error('Failed to load daily data:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDateRange = () => {
    const fmt = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    return startDate === endDate ? fmt(startDate) : `${fmt(startDate)} — ${fmt(endDate)}`;
  };

  const setToday = () => { setStartDate(today); setEndDate(today); };
  const setThisWeek = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.getFullYear(), d.getMonth(), diff);
    setStartDate(fmtLocal(monday));
    setEndDate(today);
  };
  const setThisMonth = () => {
    const d = new Date();
    setStartDate(fmtLocal(new Date(d.getFullYear(), d.getMonth(), 1)));
    setEndDate(today);
  };
  const setLast30 = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    setStartDate(fmtLocal(d));
    setEndDate(today);
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const handleDownload = () => {
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/api/reports/daily-tracker/download?date=${startDate}&token=${token}`, '_blank');
  };

  const hasSoCn = (dailySummary?.sales_orders_total > 0 || dailySummary?.credit_notes_total > 0);
  const isRange = startDate !== endDate;

  return (
    <div data-testid="daily-tracker-page">
      <PageHeader 
        title="Daily Business Tracker" 
        subtitle={formatDateRange()}
        actions={
          <Button onClick={handleDownload} className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="download-tracker-btn">
            <DownloadSimple size={16} className="mr-2" />
            Download PDF
          </Button>
        }
      />
      
      <div className="page-content">
        {/* Date Range Filter Bar */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 mb-6" data-testid="tracker-date-filters">
          <div className="flex flex-wrap items-center gap-3">
            <CalendarBlank size={18} className="text-[#0F4C81]" />
            <div className="flex items-center gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">From</label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40 h-9" data-testid="tracker-start-date" />
              </div>
              <span className="text-[#64748B] mt-4">—</span>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">To</label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40 h-9" data-testid="tracker-end-date" />
              </div>
            </div>
            <div className="h-6 w-px bg-[#E2E8F0]" />
            <div className="flex bg-[#F8FAFC] rounded-lg p-0.5">
              <button onClick={setToday} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${startDate === today && endDate === today ? 'bg-[#0F4C81] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-testid="quick-today">Today</button>
              <button onClick={setThisWeek} className="px-2.5 py-1 text-xs font-medium rounded-md text-[#64748B] hover:text-[#0F172A]" data-testid="quick-week">This Week</button>
              <button onClick={setThisMonth} className="px-2.5 py-1 text-xs font-medium rounded-md text-[#64748B] hover:text-[#0F172A]" data-testid="quick-month">This Month</button>
              <button onClick={setLast30} className="px-2.5 py-1 text-xs font-medium rounded-md text-[#64748B] hover:text-[#0F172A]" data-testid="quick-30d">Last 30 Days</button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <StatsCard
              title={isRange ? "Total Revenue" : "Net Revenue"}
              value={dailySummary?.net_revenue || dailySummary?.daily_revenue || 0}
              icon={CurrencyInr}
              subtitle={hasSoCn ? "Inv + SO - CN" : undefined}
            />
            <StatsCard
              title="Collections"
              value={dailySummary?.daily_collections || 0}
              icon={Bank}
            />
            <StatsCard
              title="Invoices"
              value={dailySummary?.invoice_count || 0}
              isCurrency={false}
              icon={Receipt}
            />
            <StatsCard
              title="Avg. Ticket"
              value={dailySummary?.average_ticket_size || 0}
              icon={TrendUp}
            />
          </div>

          {/* SO/CN Breakdown */}
          {hasSoCn && (
            <div className="grid grid-cols-3 gap-4" data-testid="daily-so-cn-breakdown">
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Receipt size={16} className="text-[#0F4C81]" />
                  <p className="text-xs text-[#64748B]">Invoice Revenue</p>
                </div>
                <p className="text-lg font-bold text-[#0F172A]" data-testid="daily-invoice-revenue">{formatCurrency(dailySummary?.daily_revenue || 0)}</p>
                <p className="text-xs text-[#64748B]">{dailySummary?.invoice_count || 0} invoices</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <ShoppingCart size={16} className="text-emerald-600" />
                  <p className="text-xs text-emerald-600">Sales Orders</p>
                </div>
                <p className="text-lg font-bold text-emerald-700" data-testid="daily-so-total">{formatCurrency(dailySummary?.sales_orders_total || 0)}</p>
                <p className="text-xs text-emerald-600">{dailySummary?.sales_orders_count || 0} orders</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard size={16} className="text-red-500" />
                  <p className="text-xs text-red-500">Credit Notes</p>
                </div>
                <p className="text-lg font-bold text-red-600" data-testid="daily-cn-total">{formatCurrency(dailySummary?.credit_notes_total || 0)}</p>
                <p className="text-xs text-red-500">{dailySummary?.credit_notes_count || 0} notes</p>
              </div>
            </div>
          )}

          {/* Outstanding */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium font-['Plus_Jakarta_Sans']" data-testid="outstanding-title">{isRange ? "Period Outstanding" : "Day's Outstanding"}</h3>
                <p className="text-sm text-[#64748B]">Unpaid invoices from this {isRange ? 'period' : 'date'}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-[#DC2626]" data-testid="day-outstanding-value">
                  {formatCurrency(dailySummary?.day_outstanding || 0)}
                </p>
                <p className="text-xs text-[#64748B]" data-testid="day-outstanding-count">
                  {dailySummary?.day_outstanding_count || 0} invoices
                </p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[#E2E8F0] flex justify-between items-center">
              <span className="text-xs text-[#64748B]">Cumulative Outstanding (up to {isRange ? 'end date' : 'date'})</span>
              <span className="text-sm font-medium text-[#64748B]" data-testid="cumulative-outstanding">{formatCurrency(dailySummary?.outstanding_receivables || 0)} ({dailySummary?.outstanding_count || 0} invoices)</span>
            </div>
          </div>

          {/* Source Breakdown */}
          {sourceBreakdown.length > 0 && (
            <div>
              <h3 className="text-lg font-medium font-['Plus_Jakarta_Sans'] mb-4">Source-wise Breakdown</h3>
              <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F8FAFC]">
                      <th className="text-left px-4 py-2.5 font-medium text-[#64748B]">Source</th>
                      <th className="text-right px-4 py-2.5 font-medium text-[#64748B]">Revenue</th>
                      <th className="text-right px-4 py-2.5 font-medium text-[#64748B]">Invoices</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceBreakdown.map((s, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0]">
                        <td className="px-4 py-2.5 font-medium">{s.source}</td>
                        <td className="px-4 py-2.5 text-right">{formatCurrency(s.revenue)}</td>
                        <td className="px-4 py-2.5 text-right">{s.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Clinic Breakdown */}
          <div>
            <h3 className="text-lg font-medium font-['Plus_Jakarta_Sans'] mb-4">Clinic-wise Breakdown</h3>
            <div className="grid grid-cols-3 gap-4">
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <div key={i} className="skeleton h-32 rounded-lg"></div>
                ))
              ) : clinicBreakdown.length > 0 ? (
                clinicBreakdown.map((clinic, index) => (
                  <div key={index} className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                    <h4 className="font-medium text-sm mb-3">{clinic.clinic}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-[#64748B]">Revenue</span>
                        <span className="font-medium">{formatCurrency(clinic.revenue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-[#64748B]">Invoices</span>
                        <span className="font-medium">{clinic.count}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-3 text-center py-8 text-[#64748B]">
                  <p>No data for selected {isRange ? 'period' : 'date'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
